import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  message,
  Popconfirm,
  Card,
  Space,
  Switch
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined
} from '@ant-design/icons';
import { categoryAPI } from '../utils/api';
import { useAuth } from '../hooks/useAuthHook';
import { validateTextField, validateDescription } from '../utils/validator';
import useDocumentTitle from '../hooks/useDocumentTitle';
import { useParams } from 'react-router-dom';

const CategoryManagement = () => {
  const { user } = useAuth();
  const { username } = useParams();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [form] = Form.useForm();

  // 根据是新建还是编辑动态设置网页标题
  const pageTitle = editingCategory ? '编辑分类' : '分类管理';
  useDocumentTitle(pageTitle, {
    prefix: '',
    suffix: ' - rtycn'
  });

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const response = await categoryAPI.getCategoriesByAuthor(user.username);
      setCategories(response.data || []);
    } catch (error) {
      message.error('获取分类列表失败');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingCategory(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingCategory(record);
    form.setFieldsValue(record);
    setModalVisible(true);
  };

  const handleDelete = async (categoryId) => {
    try {
      await categoryAPI.deleteCategory(categoryId);
      message.success('删除成功');
      fetchCategories();
    } catch (error) {
      message.error('删除失败');
    }
  };

  const handleDisable = async (categoryId, disabled) => {
    try {
      await categoryAPI.disableCategory(categoryId);
      message.success(disabled ? '禁用成功' : '启用成功');
      fetchCategories();
    } catch (error) {
      message.error('操作失败');
    }
  };

  const handleSubmit = async (values) => {
    try {
      // 前端验证
      if (values.name) {
        const nameResult = validateTextField(values.name, { required: true, fieldName: '分类名称', max: 50 });
        if (!nameResult.valid) {
          message.error(nameResult.message);
          return;
        }
      }

      if (values.description) {
        const descResult = validateDescription(values.description, { fieldName: '分类描述', max: 500 });
        if (!descResult.valid) {
          message.error(descResult.message);
          return;
        }
      }

      const submitData = {
        ...values,
        authorId: user.username,
        authorName: user.nickname || user.username
      };

      if (editingCategory) {
        await categoryAPI.updateCategory(editingCategory.id, submitData);
        message.success('更新成功');
      } else {
        await categoryAPI.createCategory(submitData);
        message.success('创建成功');
      }
      setModalVisible(false);
      fetchCategories();
    } catch (error) {
      message.error(editingCategory ? '更新失败' : '创建失败');
    }
  };

  const columns = [
    {
      title: '分类名称',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: '描述',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
    },
    {
      title: '状态',
      dataIndex: 'disabled',
      key: 'disabled',
      render: (disabled) => (
        <span style={{ color: disabled ? '#ff4d4f' : '#52c41a' }}>
          {disabled ? '已禁用' : '已启用'}
        </span>
      ),
    },
    {
      title: '创建时间',
      dataIndex: 'createTime',
      key: 'createTime',
      render: (time) => {
        if (!time) return '未知';
        const date = new Date(time);
        if (isNaN(date.getTime())) return '未知';
        return date.toLocaleString('zh-CN', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit'
        });
      },
    },
    {
      title: '操作',
      key: 'action',
      width: 300,
      render: (_, record) => (
        <Space size="small">
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            编辑
          </Button>
          <Switch
            checked={!record.disabled}
            onChange={(checked) => handleDisable(record.id, !checked)}
            size="small"
          />
          <Popconfirm
            title="确定删除这个分类吗？"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ width: '100%', maxWidth: 800, margin: '0 auto' }}>
      <Card
        title="分类管理"
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
            新建分类
          </Button>
        }
      >
        <Table
          columns={columns}
          dataSource={categories}
          loading={loading}
          rowKey="id"
          pagination={{
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 个分类`,
          }}
        />
      </Card>

      <Modal
        title={editingCategory ? '编辑分类' : '新建分类'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            name="name"
            label="分类名称"
            rules={[{ required: true, message: '请输入分类名称' }]}
          >
            <Input placeholder="请输入分类名称" />
          </Form.Item>

          <Form.Item
            name="description"
            label="分类描述"
          >
            <Input.TextArea
              placeholder="请输入分类描述"
              rows={4}
            />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button onClick={() => setModalVisible(false)}>
                取消
              </Button>
              <Button type="primary" htmlType="submit">
                {editingCategory ? '更新' : '创建'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default CategoryManagement;
